import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormEntry } from "../../../types/types";
import { formMappings } from "../../../config/formMappings";
import { RootState, AppDispatch } from "../../../store";
import { updateNotes } from "../../../services/apiService";
import { useNotification } from "../../../context/NotificationContext";
import { fetchFormEntries } from "../../../store/slices/formEntriesSlice";

export default function NotesSection(props: { entry: FormEntry }) {
  const { showNotification } = useNotification();
  const dispatch = useDispatch<AppDispatch>();

  // Get form mapping from Redux state
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];

  // Get notes field key from formMappings
  const notesFieldKey = (formMapping?.fieldMappings as any)?.notes;

  // Get notes value using the mapped field key
  const initialNotes = notesFieldKey
    ? props.entry.meta[notesFieldKey] || ""
    : "";
  const [notes, setNotes] = useState(initialNotes);
  const [originalNotes, setOriginalNotes] = useState(initialNotes);

  // Sync notes state when entry changes
  useEffect(() => {
    const currentNotes = notesFieldKey
      ? props.entry.meta[notesFieldKey] || ""
      : "";
    setNotes(currentNotes);
    setOriginalNotes(currentNotes);
  }, [props.entry, notesFieldKey]);

  const updateEntries = async () => {
    if (formMapping) {
      await dispatch(fetchFormEntries(formMapping.formId));
    }
  };

  const handleSaveNotes = async () => {
    if (props.entry && activeFormName) {
      try {
        await updateNotes(props.entry.id, notes, activeFormName);
        showNotification({
          title: "Successfully Updated Notes",
          message: "Successfully updated notes for this entry.",
          type: "success",
        });
        await updateEntries();
      } catch (error) {
        console.error("Error updating notes:", error);
        showNotification({
          title: "Update Notes Failed",
          message: "Failed to update notes for this entry.",
          type: "error",
        });
      }
    }
  };

  return (
    <section aria-labelledby={`notes-information-title`}>
      <div className="bg-white shadow rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900">Notes</h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="text-gray-700 my-4 w-full h-40 p-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          onClick={handleSaveNotes}
          className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
        >
          {originalNotes && originalNotes.trim() ? "Update Note" : "Add Note"}
        </button>
      </div>
    </section>
  );
}
